const IS_PROD_BUILD = import.meta.env.MODE === "production";

/**
 * Which Firebase project the app talks to.
 *
 * A built artifact always follows its build mode — a production build is locked
 * to the production backend, a development build to the development one — so
 * nothing that gets deployed can be pointed somewhere else by accident.
 *
 * The VITE_BACKEND=prod override is honoured ONLY by a running dev server, which
 * is why the gate is `__VITE_DEV_SERVER__` (injected by vite.config.ts) and not
 * the build mode: `yarn build:dev` also runs in "development" mode, so keying off
 * the mode would let a stray env var ship a deployed dev site wired to
 * production. Both `yarn build` and `yarn build:dev` ignore the variable.
 *
 * Redirecting the dev server is a deliberate act — `yarn dev:prod` is the only
 * thing that sets it — and the app shows a permanent red banner while it is on.
 */
const OVERRIDE_TO_PROD = __VITE_DEV_SERVER__ && import.meta.env.VITE_BACKEND === "prod";

const BACKEND: "prod" | "dev" = IS_PROD_BUILD || OVERRIDE_TO_PROD ? "prod" : "dev";

export const CONFIG = {
	MODE: import.meta.env.MODE,
	IS_PROD: IS_PROD_BUILD,
	IS_DEV: import.meta.env.MODE === "development",
	BACKEND,
	USES_PROD_BACKEND: BACKEND === "prod",
	/** Local dev server wired to the production project — real customer data.
	 *  Can only ever be true while serving, never in a built artifact. */
	IS_LOCAL_ON_PROD: OVERRIDE_TO_PROD,
	/** Human-readable, bumped by hand in package.json. Shown to the admin so a
	 *  version can be named out loud; it is not what detects a new deploy. */
	VERSION: __APP_VERSION__,
	/** The commit this bundle was built from. `useVersionCheck` compares it with
	 *  the buildId in the deployed version.json to spot a tab running old code. */
	BUILD_ID: __BUILD_ID__,
} as const;
